import React, { FC, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useAuthUI, useUser } from '@corratech/pylot-auth-manager'
import { useWishlistActions } from '@corratech/pylot-wishlist/src/Hooks/useWishlistActions'
import { WishlistDropdown } from '@corratech/pylot-wishlist/src/WishlistDropdown'
import { WishlistItemInput } from '@pylot-data/pylotschema'
import { Button } from '@corratech/pylot-ui'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import cn from 'classnames'
import formStyles from '@commonStyles/Forms.module.scss'
import s from '@pagestyles/Wishlist.module.scss'
import AddToWishlistIcon from 'stores/ooka_default/components/icons/AddToWishlist'

export type Props = {
    isWishlistDisabled: boolean
    isConfig: boolean
    isVariantOOS?: boolean
    isButtonDisabled?: boolean
}

export const AddToWishlist: FC<WishlistItemInput & Props> = ({
    sku,
    selected_options,
    quantity,
    isWishlistDisabled,
    isConfig,
    isVariantOOS,
    isButtonDisabled
}) => {
    const { t } = useTranslation(['wishlist'])
    const { isSignedIn } = useUser()
    const { handleAddWishlistAction, isLoading } = useWishlistActions()
    const { openAuthModal, onLoginCallback, onSignUpCallback } = useAuthUI()
    const [error, setError] = useState(false)
    const config = useStoreConfig()

    const addProductToWishlistHandler = (id?: number) => {
        handleAddWishlistAction(
            {
                wishlistId: id ? id : 0,
                itemOptions: [
                    {
                        sku,
                        quantity,
                        selected_options
                    }
                ]
            },
            isConfig
        )
    }

    const handleClick = () => {
        if (isWishlistDisabled || isVariantOOS) {
            setError(true)
        } else {
            addProductToWishlistHandler()
        }
    }

    const handleSignInAction = () => {
        openAuthModal()
        onLoginCallback([() => handleClick()])
        onSignUpCallback([() => handleClick()])
    }

    if (!config.base.wishlist.enabled) return null

    //show login modal for non logged-in users
    if (!isSignedIn) {
        return (
            <Button
                className={s['add-to-wishlist']}
                onClick={handleSignInAction}
                onKeyUp={handleSignInAction}
                variant="link"
            >
                <AddToWishlistIcon />
            </Button>
        )
    }

    return (
        <>
            <div className={cn(s['add-to-wishlist'], formStyles.root)}>
                <Button
                    loading={isLoading}
                    disabled={isLoading}
                    variant="link"
                    onKeyUp={handleClick}
                    onClick={handleClick}
                >
                    {!isLoading && <AddToWishlistIcon />}
                </Button>
                {!isWishlistDisabled &&
                    config.base.wishlist.multiple_wishlists && (
                        <WishlistDropdown
                            onClick={addProductToWishlistHandler}
                            onCreate={addProductToWishlistHandler}
                        />
                    )}
            </div>
            {isWishlistDisabled && error && (
                <div className={s['wishlist-error']}>
                    {t(
                        'wishlist|Please select a variant with stock to proceed.'
                    )}
                </div>
            )}
            {isVariantOOS && selected_options?.length !== 0 && error && (
                <div className={s['wishlist-error']}>
                    {t('Please select an in-stock option to continue.')}
                </div>
            )}
        </>
    )
}
